import { DeviceOperationalState } from '@/__generated__/graphql';
import ClientHiddenChip from '@components/ClientHiddenChip';
import TruncatedTypography from '@components/TruncatedTypography';
import DeviceTooltip from '@components/tooltips/DeviceTooltip';
import { Trans } from '@lingui/macro';
import {
  Box,
  ListItem,
  ListItemButton,
  Typography,
  ListItemIcon,
  Checkbox,
  CheckboxProps,
} from '@mui/material';
import { type FC } from 'react';

interface Props {
  style?: React.CSSProperties;
  lastItem?: boolean;
  deviceId: string;
  deviceName: string;
  deviceOrganization?: {
    id: number;
    name: string;
  } | null;
  deviceIrrigationName?: string;
  onClick?: () => void;
  hasAccess?: boolean;
  modified?: boolean;
  selected?: boolean;
  checkboxColor?: CheckboxProps['color'];
  /** AMARU-597/606: false → acceso concedido pero aún no visible para el cliente (vista admin). */
  visibleToUser?: boolean;
  operationalState?: DeviceOperationalState | null;
  /** AMARU-606: flag manual del device — causa alterna de ocultamiento para el tooltip. */
  deviceVisibleToClient?: boolean | null;
  /** AMARU-606: ¿el operationalState por sí solo es visible para el cliente? (resuelto por backend) */
  operationalStateVisibleToClient?: boolean | null;
}

const DevicesListItem: FC<Props> = ({
  lastItem,
  style,
  deviceId,
  deviceName,
  deviceOrganization,
  deviceIrrigationName,
  onClick,
  hasAccess: checked,
  modified,
  selected,
  checkboxColor,
  visibleToUser = true,
  operationalState,
  deviceVisibleToClient,
  operationalStateVisibleToClient,
}) => {
  const hasCheckbox = checked != null;
  return (
    <ListItem
      disablePadding
      style={style}
      sx={{
        borderBottom: lastItem ? null : '1px solid',
        borderColor: 'divider',
      }}
    >
      <DeviceTooltip deviceId={deviceId} placement="right-end">
        <ListItemButton
          selected={selected}
          onClick={onClick}
          sx={{ height: '100%', width: '100%', display: 'flex', opacity: visibleToUser ? 1 : 0.5 }}
        >
          <Box width={hasCheckbox ? 'calc(100% - 125px)' : '100%'}>
            <Box display="flex" alignItems="center" gap={2}>
              <TruncatedTypography fontSize={14} maxWidth="45%">
                {deviceName}
              </TruncatedTypography>
              {deviceOrganization ? (
                <TruncatedTypography fontSize={12} maxWidth="40%" color="text.secondary">
                  {deviceOrganization.name}
                </TruncatedTypography>
              ) : (
                <Typography
                  alignSelf="center"
                  fontSize="small"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  <Trans>Sin organización</Trans>
                </Typography>
              )}
              {!visibleToUser && (
                <ClientHiddenChip
                  operationalState={operationalState}
                  visibleToClient={deviceVisibleToClient}
                  operationalStateVisibleToClient={operationalStateVisibleToClient}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <TruncatedTypography maxWidth="30%" fontSize={12} color="text.secondary">
                ID: {deviceId}
              </TruncatedTypography>
              <TruncatedTypography maxWidth="40%" fontSize={12} color="text.secondary">
                ICN: {deviceIrrigationName}
              </TruncatedTypography>
            </Box>
          </Box>
          {hasCheckbox && (
            <Box display="flex" flex={1} justifyContent="right" alignItems="center">
              {modified && (
                <Typography fontSize="small" fontStyle="italic" color="text.secondary">
                  <Trans>(editado)</Trans>
                </Typography>
              )}
              {checked != null && (
                <ListItemIcon>
                  <Checkbox edge="end" checked={checked} tabIndex={-1} color={checkboxColor} />
                </ListItemIcon>
              )}
            </Box>
          )}
        </ListItemButton>
      </DeviceTooltip>
    </ListItem>
  );
};

export default DevicesListItem;
